import { useUpdate } from 'react-admin';
import { useGetAppConfig } from './useGetAppConfig';
import { useOnSettled } from './useOnSettled';
import { useForm } from 'react-hook-form';
import { useCallback, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Button,
  TextField,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

const schema = z.object({
  defaultAudioChunkSizeMillis: z
    .enum(['15000', '30000', '60000'])
    .pipe(z.coerce.number()),
});

export const AudioChunkSize = () => {
  const [updateConfig, { isLoading: isSaving }] = useUpdate(
    'therapistAppConfig',
    { id: 'current' },
  );

  const {
    remoteAppConfig: { defaultAudioChunkSizeMillis },
  } = useGetAppConfig();

  const { onSettled } = useOnSettled();

  const {
    register,
    handleSubmit,
    formState,
    setValue,
    reset,
    getValues,
    watch,
  } = useForm({
    resolver: zodResolver(schema as any),
    defaultValues: {
      defaultAudioChunkSizeMillis,
    },
  });

  useEffect(() => {
    setValue(
      'defaultAudioChunkSizeMillis',
      defaultAudioChunkSizeMillis,
    );
  }, [defaultAudioChunkSizeMillis, setValue]);

  const doSubmit = useCallback(
    async (data: any) => {
      await updateConfig(
        'therapistAppConfig',
        {
          id: 'current',
          data,
        },
        {
          onSettled,
        },
      );

      reset(data);
    },
    [updateConfig, onSettled, reset],
  );

  const onSubmit = handleSubmit(doSubmit);

  const changed =
    defaultAudioChunkSizeMillis !==
    getValues().defaultAudioChunkSizeMillis;

  return (
    <Box marginTop="2rem">
      <h2>Default Audio Chunk Size</h2>
      <form onSubmit={onSubmit}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
        >
          <TextField
            label="Chunk Size (milliseconds)"
            disabled={isSaving}
            error={!!formState.errors.defaultAudioChunkSizeMillis}
            InputLabelProps={{
              shrink: true,
              sx: { paddingBottom: '2rem' },
            }}
            {...register('defaultAudioChunkSizeMillis', {
              required: true,
            })}
          />
          {(formState.isDirty || formState.isSubmitting) && (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSaving || !formState.isValid}
              endIcon={isSaving && <CircularProgress size={14} />}
            >
              {isSaving ? 'Updating' : 'Update'} Audio Chunk Size
            </Button>
          )}
        </Box>
      </form>
    </Box>
  );
};
